/**
 * Admin — Embed Allowlist
 *
 * System-admin-managed whitelist of domains permitted for iframe embedding
 * in project content sections (About tab rich text editor).
 *
 * Each entry authorises a domain to render scripts inside the platform's
 * DOM — this is a high-privilege security boundary, not a convenience setting.
 *
 * English only (admin UI per CLAUDE.md).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchEmbedAllowlistFull,
  addEmbedDomain,
  updateEmbedDomain,
  deactivateEmbedDomain,
  type EmbedAllowedDomain,
} from '../../api/embedAllowlistApi';
import styles from './AdminPages.module.css';

/* ── Category options ──────────────────────────────────────────── */

const CATEGORIES = ['visualization', 'video', 'mapping', 'data'] as const;

const CATEGORY_BADGES: Record<string, { bg: string; color: string }> = {
  visualization: { bg: '#dbeafe', color: '#1e40af' },
  video:         { bg: '#fce7f3', color: '#9d174d' },
  mapping:       { bg: '#d1fae5', color: '#065f46' },
  data:          { bg: '#fef3c7', color: '#92400e' },
};

/* ── Component ─────────────────────────────────────────────────── */

export default function AdminEmbedAllowlist() {
  // ── Data state ────────────────────────────────────────
  const [domains, setDomains] = useState<EmbedAllowedDomain[]>([]);

  // ── Add form state ────────────────────────────────────
  const [newDomain, setNewDomain] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState<string>('visualization');
  const [newHeight, setNewHeight] = useState('600');
  const [newNotes, setNewNotes] = useState('');

  // ── Edit state (inline row editing) ───────────────────
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editHeight, setEditHeight] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // ── General state ─────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const statusTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Load on mount ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchEmbedAllowlistFull();
        if (!cancelled) setDomains(data);
      } catch {
        // API not ready
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Flash status ──────────────────────────────────────
  const flash = useCallback((type: 'success' | 'error', msg: string) => {
    setStatus({ type, msg });
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(null), 4000);
  }, []);

  // ── Add domain ────────────────────────────────────────
  const handleAdd = useCallback(async () => {
    const domain = newDomain.trim().toLowerCase();
    const label = newLabel.trim();
    if (!domain || !label) {
      flash('error', 'Domain and label are both required.');
      return;
    }
    if (domains.some((d) => d.domain === domain && d.isActive)) {
      flash('error', `"${domain}" is already on the allowlist.`);
      return;
    }
    try {
      const added = await addEmbedDomain({
        domain,
        label,
        category: newCategory,
        defaultHeight: parseInt(newHeight, 10) || 600,
        notes: newNotes.trim() || undefined,
      });
      setDomains((prev) => [...prev, added]);
      setNewDomain('');
      setNewLabel('');
      setNewCategory('visualization');
      setNewHeight('600');
      setNewNotes('');
      flash('success', `Added "${domain}" (${label}) to embed allowlist.`);
    } catch (err: any) {
      flash('error', `Failed to add domain: ${err.message || 'Unknown error'}`);
    }
  }, [newDomain, newLabel, newCategory, newHeight, newNotes, domains, flash]);

  // ── Start editing a row ───────────────────────────────
  const startEdit = useCallback((d: EmbedAllowedDomain) => {
    setEditingId(d.id);
    setEditLabel(d.label);
    setEditCategory(d.category);
    setEditHeight(String(d.defaultHeight));
    setEditNotes(d.notes || '');
  }, []);

  // ── Save edit ─────────────────────────────────────────
  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    try {
      const updated = await updateEmbedDomain(editingId, {
        label: editLabel.trim() || undefined,
        category: editCategory || undefined,
        defaultHeight: parseInt(editHeight, 10) || undefined,
        notes: editNotes,
      });
      setDomains((prev) => prev.map((d) => d.id === editingId ? updated : d));
      setEditingId(null);
      flash('success', `Updated "${updated.domain}".`);
    } catch (err: any) {
      flash('error', `Failed to update: ${err.message || 'Unknown error'}`);
    }
  }, [editingId, editLabel, editCategory, editHeight, editNotes, flash]);

  // ── Cancel edit ───────────────────────────────────────
  const cancelEdit = useCallback(() => setEditingId(null), []);

  // ── Toggle active/inactive ────────────────────────────
  const handleToggleActive = useCallback(async (d: EmbedAllowedDomain) => {
    const action = d.isActive ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${d.isActive ? 'Deactivate' : 'Reactivate'} "${d.domain}"?`)) return;
    try {
      if (d.isActive) {
        await deactivateEmbedDomain(d.id);
        setDomains((prev) => prev.map((x) => x.id === d.id ? { ...x, isActive: false } : x));
      } else {
        const updated = await updateEmbedDomain(d.id, { isActive: true });
        setDomains((prev) => prev.map((x) => x.id === d.id ? updated : x));
      }
      flash('success', `${d.isActive ? 'Deactivated' : 'Reactivated'} "${d.domain}".`);
    } catch (err: any) {
      flash('error', `Failed to ${action}: ${err.message || 'Unknown error'}`);
    }
  }, [flash]);

  // ── Render ────────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Embed Allowlist</h1>
            <p className={styles.pageSubtitle}>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  const activeDomains = domains.filter((d) => d.isActive);
  const inactiveDomains = domains.filter((d) => !d.isActive);

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Embed Allowlist</h1>
          <p className={styles.pageSubtitle}>
            Trusted domains that can render iframes inside project content sections. Each entry
            authorises a domain to execute scripts inside the platform&apos;s DOM — treat this as a
            security boundary.
          </p>
        </div>
      </div>

      {status && (
        <div className={`${styles.statusBar} ${status.type === 'success' ? styles.status_success : styles.status_error}`}>
          {status.msg}
        </div>
      )}

      {/* ═══ Add domain form ═══ */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Add Trusted Domain</h2>
        </div>
        <p className={styles.sectionHint}>
          Before adding a domain, verify it supports iframe embedding and that its content is
          appropriate for the platform. The domain and all subdomains will be permitted.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className={styles.fieldLabel} style={{ flex: '1 1 180px', marginBottom: 0 }}>
            Domain *
            <input
              className={styles.input}
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="e.g., ourworldindata.org"
            />
          </label>
          <label className={styles.fieldLabel} style={{ flex: '1 1 180px', marginBottom: 0 }}>
            Label *
            <input
              className={styles.input}
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="e.g., Our World in Data"
            />
          </label>
          <label className={styles.fieldLabel} style={{ flex: '0 0 140px', marginBottom: 0 }}>
            Category
            <select
              className={styles.input}
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </label>
          <label className={styles.fieldLabel} style={{ flex: '0 0 90px', marginBottom: 0 }}>
            Height (px)
            <input
              className={styles.input}
              type="number"
              value={newHeight}
              onChange={(e) => setNewHeight(e.target.value)}
              min={200}
              max={1200}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <label className={styles.fieldLabel} style={{ flex: 1, marginBottom: 0 }}>
            Notes (optional)
            <input
              className={styles.input}
              type="text"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="e.g., CC-BY licensed, 500+ SDG indicators"
            />
          </label>
          <button
            className={styles.btnAdd}
            onClick={handleAdd}
            disabled={!newDomain.trim() || !newLabel.trim()}
            style={{ whiteSpace: 'nowrap', marginBottom: 0 }}
          >
            + Add Domain
          </button>
        </div>
      </div>

      {/* ═══ Active domains table ═══ */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Active Domains</h2>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {activeDomains.length} domain{activeDomains.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'start' }}>
                {['Domain', 'Label', 'Category', 'Height', 'Notes', 'Added', 'Actions'].map((h) => (
                  <th key={h} style={{
                    padding: '8px 12px', fontWeight: 600, fontSize: 11,
                    color: '#6b7280', textTransform: 'uppercase',
                    textAlign: h === 'Actions' ? 'center' : 'start',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeDomains.map((d) => {
                const isEditing = editingId === d.id;
                const badge = CATEGORY_BADGES[d.category] || CATEGORY_BADGES.data;

                return (
                  <tr key={d.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    {/* Domain — always read-only */}
                    <td style={{ padding: '6px 12px', fontWeight: 500, color: '#1a1a2e', fontFamily: 'monospace', fontSize: 12 }}>
                      {d.domain}
                    </td>

                    {/* Label */}
                    <td style={{ padding: '6px 12px', color: '#374151' }}>
                      {isEditing ? (
                        <input
                          className={styles.input}
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          style={{ fontSize: 12, padding: '3px 6px', margin: 0 }}
                        />
                      ) : d.label}
                    </td>

                    {/* Category */}
                    <td style={{ padding: '6px 12px' }}>
                      {isEditing ? (
                        <select
                          className={styles.input}
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          style={{ fontSize: 12, padding: '3px 6px', margin: 0 }}
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          fontSize: 11, fontWeight: 600,
                          background: badge.bg, color: badge.color,
                        }}>
                          {d.category}
                        </span>
                      )}
                    </td>

                    {/* Height */}
                    <td style={{ padding: '6px 12px', color: '#6b7280', fontSize: 12, textAlign: 'center' }}>
                      {isEditing ? (
                        <input
                          className={styles.input}
                          type="number"
                          value={editHeight}
                          onChange={(e) => setEditHeight(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          style={{ fontSize: 12, padding: '3px 6px', margin: 0, width: 60 }}
                          min={200}
                          max={1200}
                        />
                      ) : `${d.defaultHeight}px`}
                    </td>

                    {/* Notes */}
                    <td style={{ padding: '6px 12px', color: '#6b7280', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <input
                          className={styles.input}
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          style={{ fontSize: 12, padding: '3px 6px', margin: 0 }}
                          placeholder="Notes…"
                        />
                      ) : (d.notes || '\u2014')}
                    </td>

                    {/* Added */}
                    <td style={{ padding: '6px 12px', color: '#9ca3af', fontSize: 11 }}>
                      {d.createdAt
                        ? new Date(d.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '\u2014'}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={saveEdit}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #059669', background: '#ecfdf5', color: '#059669', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #9ca3af', background: '#f9fafb', color: '#6b7280', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(d)}
                              title={`Edit ${d.domain}`}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #3b82f6', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(d)}
                              title={`Deactivate ${d.domain}`}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #dc2626', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {activeDomains.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
                    No active embed domains. Users cannot embed external content.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Inactive domains (if any) ═══ */}
      {inactiveDomains.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Inactive Domains</h2>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              {inactiveDomains.length} deactivated
            </span>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'start' }}>
                  {['Domain', 'Label', 'Category', 'Deactivated', 'Actions'].map((h) => (
                    <th key={h} style={{
                      padding: '8px 12px', fontWeight: 600, fontSize: 11,
                      color: '#6b7280', textTransform: 'uppercase',
                      textAlign: h === 'Actions' ? 'center' : 'start',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inactiveDomains.map((d) => (
                  <tr key={d.id} style={{ borderTop: '1px solid #f3f4f6', opacity: 0.7 }}>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>
                      {d.domain}
                    </td>
                    <td style={{ padding: '6px 12px', color: '#9ca3af' }}>{d.label}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 600, background: '#f3f4f6', color: '#9ca3af',
                      }}>
                        {d.category}
                      </span>
                    </td>
                    <td style={{ padding: '6px 12px', color: '#9ca3af', fontSize: 11 }}>
                      {d.updatedAt
                        ? new Date(d.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '\u2014'}
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleActive(d)}
                        title={`Reactivate ${d.domain}`}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #059669', background: '#ecfdf5', color: '#059669', cursor: 'pointer', fontWeight: 600 }}
                      >
                        Reactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
